import React, { forwardRef } from 'react';
import { formatPaise } from '../lib/cn';
import { cn } from '../lib/cn';
import { Button } from './Button';
import { Plus, ShoppingCart, RotateCcw } from 'lucide-react';
import { ResilientImage } from './ResilientImage';

export interface RepeatOrderItem {
  id: number | string;
  name: string;
  qty: number;
  pricePaise: number;
  photoUrl?: string;
}

export interface RepeatOrderRailProps extends React.HTMLAttributes<HTMLDivElement> {
  items: RepeatOrderItem[];
  onAddAll: (items: RepeatOrderItem[]) => void;
  onAddItem: (item: RepeatOrderItem) => void;
}

export const RepeatOrderRail = forwardRef<HTMLDivElement, RepeatOrderRailProps>(
  ({ items, onAddAll, onAddItem, className, ...props }, ref) => {
    if (!items || items.length === 0) return null;

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-xl border border-border/80 bg-card/85 p-2 sm:p-2.5 shadow-2xs space-y-1.5",
          className
        )}
        {...props}
      >
        {/* Compact Micro-Header */}
        <div className="flex items-center justify-between px-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <RotateCcw className="w-3.5 h-3.5 text-primary shrink-0" />
            <h3 className="text-xs font-bold tracking-tight text-foreground">Buy Again</h3>
            <span className="text-[10px] bg-primary/10 text-primary font-bold px-1.5 py-0.2 rounded-full">
              {items.length}
            </span>
            <span className="hidden sm:inline text-[11px] text-muted-foreground ml-1">
              Your frequent staples
            </span>
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs font-bold text-primary hover:bg-primary/10 gap-1 rounded-md"
            onClick={() => onAddAll(items)}
          >
            <ShoppingCart className="w-3 h-3" />
            <span>Reorder All ({items.length})</span>
          </Button>
        </div>

        {/* Compact Horizontal Micro-Card Rail (~48px height) */}
        <div
          className="flex overflow-x-auto gap-2 pb-0.5 -mx-1 px-1 hide-scrollbar"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <style
            dangerouslySetInnerHTML={{
              __html: `
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
          `,
            }}
          />

          {items.map((item) => (
            <div
              key={item.id}
              className="flex-none w-48 sm:w-56 h-12 rounded-lg border border-border/70 bg-background/60 hover:bg-background p-1.5 hover:border-primary/40 transition-colors flex items-center gap-2 shadow-2xs group"
            >
              <div className="w-9 h-9 rounded-md overflow-hidden bg-muted shrink-0 flex items-center justify-center border border-border/50">
                {item.photoUrl ? (
                  <ResilientImage
                    src={item.photoUrl}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ShoppingCart className="w-3.5 h-3.5 text-muted-foreground/40" />
                )}
              </div>

              <div className="flex-1 min-w-0 pr-0.5">
                <p
                  className="text-xs font-medium text-foreground truncate leading-tight group-hover:text-primary transition-colors"
                  title={item.name}
                >
                  {item.name}
                </p>
                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-0.5">
                  <span className="font-bold text-foreground font-mono">
                    {formatPaise(item.pricePaise)}
                  </span>
                  <span>•</span>
                  <span>Qty: {item.qty}</span>
                </div>
              </div>

              <Button
                variant="primary"
                size="icon"
                className="w-7 h-7 rounded-md shrink-0 bg-primary/10 hover:bg-primary text-primary hover:text-primary-foreground border border-primary/20 hover:border-primary transition-all shadow-none"
                onClick={() => onAddItem(item)}
                aria-label={`Add ${item.name} to cart`}
                title="1-Tap Add"
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

RepeatOrderRail.displayName = 'RepeatOrderRail';
